import React, { useState } from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GetAppIcon from '@mui/icons-material/GetApp';
import { standardButtonStyle } from './requestdetails/buttonStyles';

const useStyles = makeStyles(() => ({
    jsonViewerContainer: {
        position: 'relative',
        background: '#2f4f4f',
        borderRadius: '4px',
        padding: '16px',
        overflow: 'auto',
        fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
        fontSize: '0.85rem',
        lineHeight: '1.6',
        maxHeight: '600px',
        border: '1px solid #e2e8f0',
    },
    buttonContainer: {
        position: 'absolute',
        right: '16px',
        top: '16px',
        display: 'flex',
        gap: '8px',
        zIndex: 10,
    },
    jsonPre: {
        margin: 0,
        padding: '40px 16px 16px 16px',
        overflow: 'auto',
        color: '#d4d4d4',
        whiteSpace: 'pre',
        fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
        fontSize: '13px',
        lineHeight: '1.5',
        '& .json-key': {
            color: '#9cdcfe',
        },
        '& .json-string': {
            color: '#ce9178',
        },
        '& .json-number': {
            color: '#b5cea8',
        },
        '& .json-boolean': {
            color: '#569cd6',
        },
        '& .json-null': {
            color: '#569cd6',
        },
    },
}));

/**
 * JsonViewer Component
 * 
 * Displays JSON data in a formatted, syntax-highlighted view with copy and download functionality.
 * 
 * @param {Object|string} data - The JSON data to display (can be object or string)
 * @param {string} filename - Optional filename for download (default: 'data.json')
 * @param {boolean} showActions - Show/hide copy and download buttons (default: true)
 * @param {object} containerStyle - Additional styles for the container
 */
const JsonViewer = ({ 
    data, 
    filename = 'data.json',
    showActions = true,
    containerStyle = {},
    convertNumbersToStrings = true
}) => {
    const classes = useStyles();
    const [showCopySuccess, setShowCopySuccess] = useState(false);

    // Parse data if it's a string
    const jsonObject = typeof data === 'string' ? 
        (() => {
            try {
                return JSON.parse(data);
            } catch {
                // If already a valid JSON string, try parsing again, otherwise return as object
                try {
                    return JSON.parse(JSON.parse(data));
                } catch {
                    return data;
                }
            }
        })() 
        : data;

    // Helper function to convert Python-style dict strings to JSON
    const convertPythonDictToJson = (str) => {
        if (typeof str !== 'string') return str;
        
        // Check if it looks like a Python dict (starts with { and contains single quotes)
        if (str.trim().startsWith('{') && str.includes("'")) {
            try {
                // Replace single quotes with double quotes for JSON parsing
                // Be careful with escaped quotes and quotes within strings
                const jsonStr = str.replace(/'/g, '"');
                return JSON.parse(jsonStr);
            } catch {
                return str;
            }
        }
        return str;
    };

    // Recursively process object to parse nested JSON strings
    const processNestedJson = (obj) => {
        if (typeof obj === 'string') {
            // Try to parse as JSON
            try {
                const parsed = JSON.parse(obj);
                return processNestedJson(parsed);
            } catch {
                // Try to convert Python dict format
                const converted = convertPythonDictToJson(obj);
                if (converted !== obj) {
                    return processNestedJson(converted);
                }
                return obj;
            }
        } else if (typeof obj === 'number' && convertNumbersToStrings) {
            return String(obj);
        } else if (Array.isArray(obj)) {
            return obj.map(item => processNestedJson(item));
        } else if (obj !== null && typeof obj === 'object') {
            const result = {};
            for (const key in obj) {
                result[key] = processNestedJson(obj[key]);
            }
            return result;
        }
        return obj;
    };

    // Process the JSON object to handle nested JSON strings
    const processedJson = processNestedJson(jsonObject);

    // Format JSON with indentation (4 spaces)
    const formattedJson = JSON.stringify(processedJson, null, 4);

    // Syntax highlighting function
    const syntaxHighlight = (json) => {
        if (!json) return '';
        
        json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        
        return json.replace(
            /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
            (match) => {
                let cls = 'json-number';
                if (/^"/.test(match)) {
                    if (/:$/.test(match)) {
                        cls = 'json-key';
                    } else {
                        cls = 'json-string';
                    }
                } else if (/true|false/.test(match)) {
                    cls = 'json-boolean';
                } else if (/null/.test(match)) {
                    cls = 'json-null';
                }
                return '<span class="' + cls + '">' + match + '</span>';
            }
        );
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(formattedJson);
        setShowCopySuccess(true);
        setTimeout(() => {
            setShowCopySuccess(false);
        }, 1500);
    };

    const handleDownload = () => {
        const blob = new Blob([formattedJson], { type: 'application/json' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename.endsWith('.json') ? filename : `${filename}.json`;
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    return (
        <Box className={classes.jsonViewerContainer} sx={containerStyle}>
            {showActions && (
                <div className={classes.buttonContainer}>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<GetAppIcon />}
                        onClick={handleDownload}
                        sx={standardButtonStyle}
                    >
                        Download
                    </Button>
                    <Tooltip open={showCopySuccess} title="Copied!" arrow>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={handleCopy}
                            sx={standardButtonStyle}
                        >
                            Copy
                        </Button>
                    </Tooltip>
                </div>
            )}
            <pre 
                className={classes.jsonPre}
                dangerouslySetInnerHTML={{ __html: syntaxHighlight(formattedJson) }}
            />
        </Box>
    );
};

export default JsonViewer;
